'use client';

import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
const _excluded = ["count", "wrapper", "className", "containerClassName", "containerTestId", "circle", "style"],
  _excluded2 = ["children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';

/**
 * @internal
 */
const SkeletonThemeContext = React.createContext({});

/* eslint-disable react/no-array-index-key */
const defaultEnableAnimation = true;
// For performance & cleanliness, don't add any inline styles unless we have to
function styleOptionsToCssProperties(_ref) {
  let {
    baseColor,
    highlightColor,
    width,
    height,
    borderRadius,
    circle,
    direction,
    duration,
    enableAnimation = defaultEnableAnimation,
    customHighlightBackground
  } = _ref;
  const style = {};
  if (direction === 'rtl') style['--animation-direction'] = 'reverse';
  if (typeof duration === 'number') style['--animation-duration'] = "".concat(duration, "s");
  if (!enableAnimation) style['--pseudo-element-display'] = 'none';
  if (typeof width === 'string' || typeof width === 'number') style.width = width;
  if (typeof height === 'string' || typeof height === 'number') style.height = height;
  if (typeof borderRadius === 'string' || typeof borderRadius === 'number') style.borderRadius = borderRadius;
  if (circle) style.borderRadius = '50%';
  if (typeof baseColor !== 'undefined') style['--base-color'] = baseColor;
  if (typeof highlightColor !== 'undefined') style['--highlight-color'] = highlightColor;
  if (typeof customHighlightBackground === 'string') style['--custom-highlight-background'] = customHighlightBackground;
  return style;
}
function Skeleton(_ref2) {
  let {
      count = 1,
      wrapper: Wrapper,
      className: customClassName,
      containerClassName,
      containerTestId,
      circle = false,
      style: styleProp
    } = _ref2,
    originalPropsStyleOptions = _objectWithoutProperties(_ref2, _excluded);
  var _a, _b, _c;
  const contextStyleOptions = React.useContext(SkeletonThemeContext);
  const propsStyleOptions = _objectSpread({}, originalPropsStyleOptions);
  // DO NOT overwrite style options from the context if `propsStyleOptions`
  // has properties explicity set to undefined
  for (const [key, value] of Object.entries(originalPropsStyleOptions)) {
    if (typeof value === 'undefined') {
      delete propsStyleOptions[key];
    }
  }
  // Props take priority over context
  const styleOptions = _objectSpread(_objectSpread(_objectSpread({}, contextStyleOptions), propsStyleOptions), {}, {
    circle
  });
  // `styleProp` has the least priority out of everything
  const style = _objectSpread(_objectSpread({}, styleProp), styleOptionsToCssProperties(styleOptions));
  let className = 'react-loading-skeleton';
  if (customClassName) className += " ".concat(customClassName);
  const inline = (_a = styleOptions.inline) !== null && _a !== void 0 ? _a : false;
  const elements = [];
  const countCeil = Math.ceil(count);
  for (let i = 0; i < countCeil; i++) {
    let thisStyle = style;
    if (countCeil > count && i === countCeil - 1) {
      // count is not an integer and we've reached the last iteration of
      // the loop, so add a "fractional" skeleton.
      //
      // For example, if count is 3.5, we've already added 3 full
      // skeletons, so now we add one more skeleton that is 0.5 times the
      // original width.
      const width = (_b = thisStyle.width) !== null && _b !== void 0 ? _b : '100%'; // 100% is the default since that's what's in the CSS
      const fractionalPart = count % 1;
      const fractionalWidth = typeof width === 'number' ? width * fractionalPart : "calc(".concat(width, " * ").concat(fractionalPart, ")");
      thisStyle = _objectSpread(_objectSpread({}, thisStyle), {}, {
        width: fractionalWidth
      });
    }
    const skeletonSpan = React.createElement("span", {
      className: className,
      style: thisStyle,
      key: i
    }, "\u200C");
    if (inline) {
      elements.push(skeletonSpan);
    } else {
      // Without the <br />, the skeleton lines will all run together if
      // `width` is specified
      elements.push(React.createElement(React.Fragment, {
        key: i
      }, skeletonSpan, React.createElement("br", null)));
    }
  }
  return React.createElement("span", {
    className: containerClassName,
    "data-testid": containerTestId,
    "aria-live": "polite",
    "aria-busy": (_c = styleOptions.enableAnimation) !== null && _c !== void 0 ? _c : defaultEnableAnimation
  }, Wrapper ? elements.map((el, i) => React.createElement(Wrapper, {
    key: i
  }, el)) : elements);
}
function SkeletonTheme(_ref3) {
  let {
      children
    } = _ref3,
    styleOptions = _objectWithoutProperties(_ref3, _excluded2);
  return React.createElement(SkeletonThemeContext.Provider, {
    value: styleOptions
  }, children);
}
export { SkeletonTheme, Skeleton as default };